/**
 * Optimized Routes Configuration for Partner Portal
 * 
 * Features:
 * - Feature-based organization
 * - Meta information for all routes
 * - Vite-optimized dynamic imports
 * - Consistent naming conventions
 * - Route guards where needed
 * - Better code splitting strategy
 */

// =============================================================================
// AUTH & ONBOARDING ROUTES
// =============================================================================
const authRoutes = [
    {
        path: '/login',
        name: 'ProjectLogin',
        component: () => import('@/views/ProjectLogin'),
        meta: {
            requiresAuth: false,
            layout: 'auth',
            title: 'Log ind'
        }
    },
    {
        path: '/glemte-password',
        name: 'ForgotPassword',
        component: () => import('@/views/ForgotPassword')
    },
    {
        path: '/nulstille-kodeord',
        name: 'ResetPassword',
        component: () => import('@/views/micrositesv1/reset-password'),
        meta: {
            requiresAuth: false,
            layout: 'auth',
            title: 'Nulstil adgangskode'
        }
    },
    {
        path: '/security',
        name: 'Security',
        component: () => import('@/views/Security'),
        meta: {
            requiresAuth: false,
            layout: 'auth',
            title: 'Sikkerhed'
        }
    },
    {
        path: '/password',
        name: 'Password',
        component: () => import('@/views/Password'),
        meta: {
            requiresAuth: true,
            title: 'Skift adgangskode'
        }
    },
    {
        path: '/partnerfri-setup',
        name: 'PartnerSetup',
        component: () => import('@/views/PartnerSetup'),
        meta: {
            requiresAuth: false,
            layout: 'auth',
            title: 'Partner opsætning'
        }
    },
    {
        path: '/questionnaire',
        name: 'Questionnaire',
        component: () => import('@/views/Questionnaire'),
        meta: {
            requiresAuth: false,
            title: 'Spørgeskema'
        }
    },
    {
        path: '/questionnaire-thankyou',
        name: 'QuestionnaireThankyou',
        component: () => import('@/views/QuestionnaireThankyou'),
        meta: {
            requiresAuth: false,
            title: 'Tak for dit svar'
        }
    }
]

// =============================================================================
// MICROSITES & PUBLIC ROUTES
// =============================================================================
const micrositeRoutes = [
    {
        path: '/signup/:pathMatch(.*)*',
        name: 'Signup',
        component: () => import('@/views/micrositesv1/signupv2'),
        meta: {
            requiresAuth: false,
            layout: 'microsite',
            title: 'Tilmeld dig'
        }
    },
    {
        path: '/signupv2/:pathMatch(.*)*',
        name: 'Signupv2',
        component: () => import('@/views/micrositesv1/signupv2'),
        meta: {
            requiresAuth: false,
            layout: 'microsite',
            title: 'Tilmeld dig'
        }
    },
    {
        path: '/upgrade/:pathMatch(.*)*',
        name: 'SignupAIText',
        component: () => import('@/views/micrositesv1/signup_AI_text'),
        meta: {
            requiresAuth: false,
            layout: 'microsite',
            title: 'Opgrader'
        }
    },
    {
        path: '/demo/:pathMatch(.*)*',
        name: 'Demo',
        component: () => import('@/views/micrositesv1/demov2'),
        meta: {
            requiresAuth: false,
            layout: 'microsite',
            title: 'Demo'
        }
    },
    {
        path: '/demov2/:pathMatch(.*)*',
        name: 'Demov2',
        component: () => import('@/views/micrositesv1/demov2'),
        meta: {
            requiresAuth: false,
            layout: 'microsite',
            title: 'Demo'
        }
    },
    {
        path: '/invo/:pathMatch(.*)*',
        name: 'Invo',
        component: () => import('@/views/micrositesv1/invo'),
        meta: {
            requiresAuth: false,
            layout: 'microsite',
            title: 'Invitation'
        }
    },
    {
        path: '/facebook',
        name: 'FacebookIntegration',
        component: () => import('@/views/micrositesv1/facebook'),
        meta: {
            requiresAuth: false,
            layout: 'microsite',
            title: 'Facebook Integration'
        }
    },
    {
        path: '/package/:pathMatch(.*)*',
        name: 'Package',
        component: () => import('@/views/micrositesv1/package'),
        meta: {
            requiresAuth: false,
            layout: 'microsite',
            title: 'Pakke'
        }
    },
    {
        path: '/singupcvr/:pathMatch(.*)*',
        name: 'Singupcvr',
        component: () => import('@/views/micrositesv1/singupcvr'),
        meta: {
            requiresAuth: false,
            layout: 'microsite',
            title: 'Tilmelding med CVR'
        }
    },
    {
        path: '/inviterepartner/:pathMatch(.*)*',
        name: 'Inviterepartner',
        component: () => import('@/views/micrositesv1/inviterepartner'),
        meta: {
            requiresAuth: false,
            layout: 'microsite',
            title: 'Inviter partner'
        }
    },
    {
        path: '/mobilepay/:pathMatch(.*)*',
        name: 'Acceptancemobilepay',
        component: () => import('@/views/micrositesv1/mobilepay'),
        meta: {
            requiresAuth: false,
            layout: 'microsite',
            title: 'MobilePay'
        }
    },
    {
        path: '/kontakt/:pathMatch(.*)*',
        name: 'Callme',
        component: () => import('@/views/micrositesv1/callme'),
        meta: {
            requiresAuth: false,
            layout: 'microsite',
            title: 'Kontakt os'
        }
    },
    {
        path: '/unsubscribe/:pathMatch(.*)*',
        name: 'Unsubscribe',
        component: () => import('@/views/micrositesv1/unsubscribe'),
        meta: {
            requiresAuth: false,
            layout: 'microsite',
            title: 'Afmeld'
        }
    }
]

// =============================================================================
// CORE PORTAL ROUTES
// =============================================================================
const coreRoutes = [
    {
        path: '/',
        redirect: '/newsfeed',
        name: 'index',
        meta: {
            requiresAuth: true
        }
    },
    {
        path: '/newsfeed',
        name: 'Newsfeed',
        component: () => import('@/views/portal/NewsfeedRouter'),
        meta: {
            requiresAuth: true,
            title: 'Nyhedsfeed',
            icon: 'newsfeed'
        }
    },
    {
        path: '/oversigt',
        name: 'Oversigt',
        component: () => import('@/views/portal/MainPage'),
        meta: {
            requiresAuth: true,
            title: 'Oversigt',
            icon: 'dashboard'
        }
    },
    {
        path: '/balance',
        name: 'Balance',
        component: () => import('@/views/portal/Balance'),
        meta: {
            requiresAuth: true,
            title: 'Balance',
            icon: 'balance'
        }
    },
    {
        path: '/recommend',
        name: 'Anbefal os',
        component: () => import('@/views/portal/recommend'),
        meta: {
            requiresAuth: true,
            title: 'Anbefal os'
        }
    },
    {
        path: '/skriv-til-support',
        name: 'Skriv til support',
        component: () => import('@/views/portal/WriteSupport'),
        meta: {
            requiresAuth: true,
            title: 'Skriv til support'
        }
    },
    {
        path: '/hjemmeside',
        name: 'Hjemmeside',
        component: () => import('@/views/portal/Website'),
        meta: {
            requiresAuth: true,
            title: 'Hjemmeside'
        }
    },
    {
        path: '/faq',
        name: 'faq',
        component: () => import('@/views/portal/faq'),
        meta: {
            requiresAuth: true,
            title: 'FAQ'
        }
    },
    {
        path: '/version',
        name: 'Version',
        component: () => import('@/views/portal/version'),
        meta: {
            requiresAuth: true,
            title: 'Version'
        }
    }
]

// =============================================================================
// MARKETPLACE ROUTES
// =============================================================================
const marketplaceRoutes = [
    {
        path: '/markedsplads',
        name: 'PrivatMarkedsplads',
        component: () => import('@/views/portal/MarketplaceRouter'),
        meta: {
            requiresAuth: true,
            title: 'Markedsplads',
            icon: 'marketplace'
        }
    },
    {
        path: '/markedsplads/quickmatch',
        name: 'quickMarkedsplads',
        component: () => import('@/views/portal/MarketplaceQuickMatch'),
        // redirect: '/markedsplads',
        meta: {
            requiresAuth: true,
            title: 'Markedsplads',
            icon: 'marketplace'
        }
    },
    {
        path: '/markedsplads-new',
        name: 'PrivatMarkedspladsNew',
        component: () => import('@/views/portal/Marketplacev2'),
        meta: {
            requiresAuth: true,
            title: 'Markedsplads (Beta)',
            betaAccess: true
        },
        beforeEnter: (to, from, next) => {
            const haaid = localStorage.getItem('haaid')
            const allowedIds = [72066, 10247, 142368]
            if (!allowedIds.includes(Number(haaid))) {
                next('/markedsplads')
                return
            }
            next()
        }
    },
    {
        path: '/markedsplads-old',
        name: 'PrivatMarkedspladsOld',
        component: () => import('@/views/portal/Marketplace'),
        meta: {
            requiresAuth: true,
            title: 'Markedsplads (Gammel)',
            deprecated: true,
            betaAccess: true
        },
        beforeEnter: (to, from, next) => {
            const haaid = localStorage.getItem('haaid')
            const allowedIds = [72066, 10247, 142368]
            if (!allowedIds.includes(Number(haaid))) {
                next('/markedsplads')
                return
            }
            next()
        }
    },
    {
        path: '/privat-udbud',
        name: 'Privat Udbud',
        component: () => import('@/views/portal/Marketplace'),
        meta: {
            requiresAuth: true,
            title: 'Privat udbud'
        }
    },
    {
        path: '/privat-udbud-bitrix',
        name: 'Privat Udbud2',
        component: () => import('@/views/portal/Marketplace2'),
        meta: {
            requiresAuth: true,
            title: 'Privat udbud (Bitrix)'
        }
    },
    {
        path: '/offentlige-udbud',
        name: 'Offentlige Udbud',
        component: () => import('@/views/portal/udbud'),
        meta: {
            requiresAuth: true,
            title: 'Offentlige udbud'
        }
    },
    {
        path: '/opgaveinvitationer',
        name: 'Openminbolig2job',
        component: () => import('@/views/portal/MarketplaceMinbolig2'),
        meta: {
            requiresAuth: true,
            title: 'Opgave invitationer'
        }
    },
    {
        path: '/ted_status',
        name: 'TedUdbud',
        component: () => import('@/views/portal/ted_udbud'),
        meta: {
            requiresAuth: true,
            title: 'TED Udbud'
        }
    },
    {
        path: '/koeb-projekter',
        name: 'Purchase',
        component: () => import('@/views/portal/Purchase'),
        meta: {
            requiresAuth: true,
            title: 'Køb projekter'
        }
    }
]

// =============================================================================
// PROJECT & MEETING MANAGEMENT ROUTES
// =============================================================================
const projectRoutes = [
    {
        path: '/tilfoej-projekter',
        name: 'Tilføj Projekter',
        component: () => import('@/views/portal/AddProjects'),
        meta: {
            requiresAuth: true,
            title: 'Tilføj projekter'
        }
    },
    {
        path: '/opret-projekt-new',
        name: 'createproject',
        component: () => import('@/views/portal/OpretProjektNew'),
        meta: {
            requiresAuth: true,
            title: 'Opret projekt'
        }
    },
    {
        path: '/opret-projekt-new/wizard',
        name: 'CreateProjectNewv2',
        component: () => import('@/views/portal/opretprojektnew/CreateProjectNew'),
        meta: {
            requiresAuth: true,
            title: 'Opret projekt - Wizard'
        }
    },
    {
        path: '/opret-projekt-new/tak',
        name: 'CreateProjectNewv2tak',
        component: () => import('@/views/portal/opretprojektnew/CreateProject_Tak'),
        meta: {
            requiresAuth: true,
            title: 'Projekt oprettet'
        }
    },
    {
        path: '/opret-projekt-new/rediger_projekt',
        name: 'CreateProjectNewvEditPage',
        component: () => import('@/views/portal/opretprojektnew/CreateProject_edit'),
        meta: {
            requiresAuth: true,
            title: 'Rediger projekt'
        }
    },
    {
        path: '/opret-projekt-new/billeder',
        name: 'CreateProjectNewuploadbilleder',
        component: () => import('@/views/portal/opretprojektnew/uploadimage'),
        meta: {
            requiresAuth: true,
            title: 'Upload billeder'
        }
    },
    {
        path: '/status-haandvaerker',
        name: 'Haandvaerker - Status',
        component: () => import('@/views/portal/NewHaandvaerkerProjectStatus'),
        meta: {
            requiresAuth: true,
            title: 'Håndværker status'
        }
    },
    {
        path: '/status-haandvaerker-new',
        name: 'Projekter',
        component: () => import('@/views/portal/NewHaandvaerkerProjectStatus2'),
        meta: {
            requiresAuth: true,
            title: 'Projekter'
        }
    },
    {
        path: '/customer-projects',
        name: 'CustomerProjects',
        component: () => import('@/views/portal/CustomerProjects'),
        meta: {
            requiresAuth: true,
            title: 'Kundeprojekter'
        }
    },
    {
        path: '/status-producenter',
        name: 'Producenter - Status',
        component: () => import('@/views/portal/NewProjectStatus'),
        meta: {
            requiresAuth: true,
            title: 'Producenter status'
        }
    },
    {
        path: '/new-meetings',
        name: 'New Meetings',
        component: () => import('@/views/portal/NewMeetings'),
        meta: {
            requiresAuth: true,
            title: 'Nye møder'
        }
    },
    {
        path: '/alle-moeder',
        name: 'mycustomer',
        component: () => import('@/views/portal/MyCustomer'),
        meta: {
            requiresAuth: true,
            title: 'Alle møder',
            icon: 'meetings'
        }
    },
    {
        path: '/alle-moeder-minbolig',
        name: 'Alle møder-minbolig',
        component: () => import('@/views/portal/AllMeetings_minbolig'),
        meta: {
            requiresAuth: true,
            title: 'Alle møder - MinBolig'
        }
    },
    {
        path: '/kundeoversigt',
        name: 'Opgaveoversigt',
        component: () => import('@/views/portal/Opgaveoversigt'),
        meta: {
            requiresAuth: true,
            title: 'Kundeoversigt'
        }
    },
    {
        path: '/approval',
        name: 'Kundebekræftelse',
        component: () => import('@/views/portal/approval'),
        meta: {
            requiresAuth: true,
            title: 'Kundebekræftelse'
        }
    },
    {
        path: '/approval/nps',
        name: 'NPS',
        component: () => import('@/views/portal/nps'),
        meta: {
            requiresAuth: true,
            title: 'NPS'
        }
    },
    {
        path: '/reject-offer',
        name: 'RejectOffer',
        component: () => import('@/views/portal/RejectOffer'),
        meta: {
            requiresAuth: true,
            title: 'Afvis tilbud'
        }
    },
    {
        path: '/property-owner',
        name: 'Property owner',
        component: () => import('@/views/portal/PropertyOwner'),
        meta: {
            requiresAuth: true,
            title: 'Ejendomsejer'
        }
    }
]

// =============================================================================
// MESTER (MASTER) ROUTES
// =============================================================================
const mesterRoutes = [
    {
        path: '/mester-arbejdskraft',
        name: 'laborview',
        component: () => import('@/views/portal/MesterArbejdskraft'),
        meta: {
            requiresAuth: true,
            title: 'Mester - Arbejdskraft',
            permission: 'mester'
        }
    },
    {
        path: '/mester-opgaver',
        name: 'Opgaver',
        component: () => import('@/views/portal/MesterOpgaver'),
        meta: {
            requiresAuth: true,
            title: 'Mester - Opgaver',
            permission: 'mester'
        }
    }
]

// =============================================================================
// CONTRACT & CUSTOMER PROJECT ROUTES
// =============================================================================
const contractRoutes = [
    {
        path: '/contracts',
        name: 'Contracts',
        component: () => import('@/views/portal/contracts'),
        meta: {
            requiresAuth: true,
            title: 'Kontrakter'
        }
    },
    {
        path: '/annullere_kontrakten',
        name: 'cancelContract',
        component: () => import('@/views/portal/contractCancellation'),
        meta: {
            requiresAuth: true,
            title: 'Annuller kontrakt'
        }
    },
    {
        path: '/customer-projects/timeline',
        name: 'Timeline',
        component: () => import('@/views/portal/customerprojects/timeline'),
        meta: {
            requiresAuth: true,
            title: 'Tidslinje'
        }
    },
    {
        path: '/customer-projects/subcontract',
        name: 'receiveSubcontract',
        component: () => import('@/views/portal/customerprojects/inviteSubcontractorReceive'),
        meta: {
            requiresAuth: true,
            title: 'Modtag underkontrakt'
        }
    },
    {
        path: '/customer-projects/subcontract-invite',
        name: 'inviteSubcontractor',
        component: () => import('@/views/portal/customerprojects/inviteSubcontractor'),
        meta: {
            requiresAuth: true,
            title: 'Inviter underentreprenør'
        }
    },
    {
        path: '/customer-projects/supplier-invite',
        name: 'inviteSupplier',
        component: () => import('@/views/portal/customerprojects/inviteSupplier'),
        meta: {
            requiresAuth: true,
            title: 'Inviter leverandør'
        }
    },
    {
        path: '/customer-projects/fastcontract-draft',
        name: 'fastcontract',
        component: () => import('@/views/portal/customerprojects/contract-fastV3'),
        meta: {
            requiresAuth: true,
            title: 'Hurtig kontrakt'
        }
    },
    {
        path: '/customer-projects/project-dashboard',
        name: 'ProjectsDashboard',
        component: () => import('@/views/portal/customerprojects/ProjectDashboard'),
        meta: {
            requiresAuth: true,
            title: 'Projekt dashboard'
        }
    },
    {
        path: '/customer-projects/approved-projects',
        name: 'ApprovedProjects',
        component: () => import('@/views/portal/customerprojects/ApprovedProjects'),
        meta: {
            requiresAuth: true,
            title: 'Godkendte projekter'
        }
    },
    {
        path: '/customer-projects/contract',
        name: 'Contract',
        component: () => import('@/views/portal/customerprojects/contract'),
        meta: {
            requiresAuth: true,
            title: 'Kontrakt'
        }
    },
    {
        path: '/customer-projects/calendar',
        name: 'Calendar',
        component: () => import('@/views/portal/customerprojects/calendar'),
        meta: {
            requiresAuth: true,
            title: 'Kalender'
        }
    },
    {
        path: '/customer-projects/contract-manager',
        name: 'ContractManager',
        component: () => import('@/views/portal/customerprojects/contract-manager'),
        meta: {
            requiresAuth: true,
            title: 'Kontraktstyring'
        }
    },
    {
        path: '/customer-projects/contract-main',
        name: 'ContractMain',
        component: () => import('@/views/portal/customerprojects/contract-main'),
        meta: {
            requiresAuth: true,
            title: 'Kontrakt hovedside'
        }
    },
    {
        path: '/customer-projects/contract-main-tak',
        name: 'ContractMainTak',
        component: () => import('@/views/portal/customerprojects/contract-main-tak'),
        meta: {
            requiresAuth: true,
            title: 'Kontrakt bekræftelse'
        }
    },
    {
        path: '/customer-projects/contract-mainv2',
        name: 'ContractMainv2',
        component: () => import('@/views/portal/customerprojects/contract-mainv2'),
        meta: {
            requiresAuth: true,
            title: 'Kontrakt hovedside v2'
        }
    },
    {
        path: '/customer-projects/contract-draft-old',
        name: 'draftContract-old',
        component: () => import('@/views/portal/customerprojects/contract_draftv2'),
        meta: {
            requiresAuth: true,
            title: 'Kontrakt udkast (gammel)',
            deprecated: true
        }
    },
    {
        path: '/customer-projects/contract-draft',
        name: 'draftContract',
        component: () => import('@/views/portal/customerprojects/contract_draftv2'),
        meta: {
            requiresAuth: true,
            title: 'Kontrakt udkast'
        }
    },
    {
        path: '/customer-projects/offer-page',
        name: 'OfferPage',
        component: () => import('@/views/portal/customerprojects/offer-page'),
        meta: {
            requiresAuth: true,
            title: 'Tilbudsside'
        }
    },
    {
        path: '/customer-projects/contract-addon',
        name: 'draftContractXtrawork',
        component: () => import('@/views/portal/customerprojects/contract_extrawork_addon'),
        meta: {
            requiresAuth: true,
            title: 'Kontrakt tillægsarbejde'
        }
    },
    {
        path: '/customer-projects/payment-management',
        name: 'Paymentmanagement',
        component: () => import('@/views/portal/customerprojects/payment-management'),
        meta: {
            requiresAuth: true,
            title: 'Betalingsstyring'
        }
    },
    {
        path: '/customer-projects/mangel-liste',
        name: 'MangelList',
        component: () => import('@/views/portal/customerprojects/mangel-list'),
        meta: {
            requiresAuth: true,
            title: 'Mangelliste'
        }
    },
    {
        path: '/customer-projects/projekt-billeder',
        name: 'ProjectImages',
        component: () => import('@/views/portal/customerprojects/projekt-billeder'),
        meta: {
            requiresAuth: true,
            title: 'Projekt billeder'
        }
    },
    {
        path: '/customer-projects/documents',
        name: 'Documents',
        component: () => import('@/views/portal/customerprojects/documents'),
        meta: {
            requiresAuth: true,
            title: 'Dokumenter'
        }
    },
    {
        path: '/customer-projects/tender',
        name: 'tender',
        component: () => import('@/views/portal/customerprojects/tenderFolderv2'),
        meta: {
            requiresAuth: true,
            title: 'Udbudsmappe'
        }
    },
    {
        path: '/addendum',
        name: 'Addendum',
        component: () => import('@/views/portal/addendum'),
        meta: {
            requiresAuth: true,
            title: 'Tillæg'
        }
    },
    {
        path: '/worksheet',
        name: 'Worksheet',
        component: () => import('@/views/portal/worksheet'),
        meta: {
            requiresAuth: true,
            title: 'Arbejdsseddel'
        }
    }
]

// =============================================================================
// TILBUD (OFFER) ROUTES
// =============================================================================
const tilbudRoutes = [
    {
        path: '/tilbud/tilbud-historie',
        name: 'Tilbudshistorie',
        component: () => import('@/views/portal/StandardTilbudhistory'),
        meta: {
            requiresAuth: true,
            title: 'Tilbudshistorie'
        }
    },
    {
        path: '/tilbud/inviter-kunde',
        name: 'inviterKunde',
        component: () => import('@/views/portal/inviteKunde.vue'),
        meta: {
            requiresAuth: true,
            title: 'Inviter kunde'
        }
    },
    // {
    //     path: '/tilbud/inviter-kunde/udvidet',
    //     name: 'inviterKunde',
    //     component: () => import('@/views/portal/inviteKunde.vue'),
    //     meta: {
    //         requiresAuth: true,
    //         title: 'Inviter kunde'
    //     }
    // },
    // {
    //     path: '/tilbud/inviter-kunde/upload',
    //     name: 'inviterKunde',
    //     component: () => import('@/views/portal/inviteKunde.vue'),
    //     meta: {
    //         requiresAuth: true,
    //         title: 'Inviter kunde'
    //     }
    // },
    // {
    //     path: '/tilbud/inviter-kunde/hurtigt',
    //     name: 'inviterKunde',
    //     component: () => import('@/views/portal/inviteKunde.vue'),
    //     meta: {
    //         requiresAuth: true,
    //         title: 'Inviter kunde'
    //     }
    // },
    {
        path: '/tilbud/standardTilbud',
        name: 'StandardTilbud',
        component: () => import('@/views/portal/standardTilbud'),
        meta: {
            requiresAuth: true,
            title: 'Standard tilbud'
        }
    },
    {
        path: '/tilbud/hurtigt-tilbud',
        name: 'HurtigtTilbud',
        component: () => import('@/views/portal/standardTilbud'),
        meta: {
            requiresAuth: true,
            title: 'Hurtigt tilbud'
        }
    },
    {
        path: '/tilbud/upload-tilbud',
        name: 'UploadTilbud',
        component: () => import('@/views/portal/standardTilbud'),
        meta: {
            requiresAuth: true,
            title: 'Upload tilbud'
        }
    },
    {
        path: '/tilbud/standardTilbud-forcedOffer',
        name: 'standardTilbud_forced',
        redirect: '/tilbud/standardTilbudProduct',
        meta: {
            requiresAuth: true
        }
    },
    {
        path: '/tilbud/standardTilbudProduct',
        name: 'StandardTilbudProduct',
        component: () => import('@/views/portal/standardTilbud-forcedoffer'),
        meta: {
            requiresAuth: true,
            title: 'Standard tilbud produkt'
        }
    },
    {
        path: '/tilbud/jobskabelse',
        name: 'StandardTilbudProduct_createproject',
        component: () => import('@/views/portal/standardTilbudProduct_createproject'),
        meta: {
            requiresAuth: true,
            title: 'Opret projekt fra tilbud'
        }
    },
    {
        path: '/tilbud/upload',
        name: 'StandardTilbudProductUpload',
        component: () => import('@/views/portal/standardTilbudProduct_upload'),
        meta: {
            requiresAuth: true,
            title: 'Upload tilbudsprodukt'
        }
    },
    {
        path: '/tilbud/prisberegner',
        name: 'PriceCalculator',
        component: () => import('@/views/portal/PriceCalculator'),
        meta: {
            requiresAuth: true,
            title: 'Prisberegner'
        }
    }
]

// =============================================================================
// ANBEFALINGER (RECOMMENDATIONS) ROUTES
// =============================================================================
const anbefalingerRoutes = [
    {
        path: '/anbefalinger',
        name: 'Anbefalinger',
        component: () => import('@/views/portal/Anbefalinger'),
        meta: {
            requiresAuth: true,
            title: 'Anbefalinger'
        }
    },
    {
        path: '/indhent-anbefalinger',
        name: 'Indhent Anbefalinger',
        component: () => import('@/views/portal/IndhentAnbefalinger'),
        meta: {
            requiresAuth: true,
            title: 'Indhent anbefalinger'
        }
    },
    {
        path: '/laes-og-besvar-anbefalinger',
        name: 'Læs og besvar',
        component: () => import('@/views/portal/ReadAnbefalinger'),
        meta: {
            requiresAuth: true,
            title: 'Læs og besvar anbefalinger'
        }
    },
    {
        path: '/anbefaling-boligejere',
        name: 'anbefalktohomeowners',
        component: () => import('@/views/portal/anbefalktohomeowners'),
        meta: {
            requiresAuth: true,
            title: 'Anbefaling til boligejere'
        }
    },
    {
        path: '/anbefalinger-sammendrag',
        name: 'Anbefaling',
        component: () => import('@/views/portal/Anbefaling'),
        meta: {
            requiresAuth: true,
            title: 'Anbefalinger sammendrag'
        }
    }
]

// =============================================================================
// SETTINGS ROUTES
// =============================================================================
const settingsRoutes = [
    {
        path: '/settings',
        name: 'Settings',
        component: () => import('@/views/portal/AllSettings'),
        meta: {
            requiresAuth: true,
            title: 'Indstillinger',
            icon: 'settings'
        }
    },
    {
        path: '/dine-indstillinger',
        name: 'Dine Indstillinger',
        component: () => import('@/views/portal/ProfileSettings'),
        meta: {
            requiresAuth: true,
            title: 'Dine indstillinger'
        }
    },
    {
        path: '/auto-anbefalinger',
        name: 'Dine Integrationer',
        component: () => import('@/views/portal/integrationSettings'),
        meta: {
            requiresAuth: true,
            title: 'Integrationer'
        }
    },
    {
        path: '/widgets',
        name: 'Widgets',
        component: () => import('@/views/portal/Widgets'),
        meta: {
            requiresAuth: true,
            title: 'Widgets'
        }
    },
    {
        path: '/dine-opgavetyper',
        name: 'Opgavetyper',
        component: () => import('@/views/portal/Opgavetyper'),
        meta: {
            requiresAuth: true,
            title: 'Opgavetyper'
        }
    },
    {
        path: '/jobforudsigelser',
        name: 'JobPredictions',
        component: () => import('@/views/portal/JobPredictions'),
        meta: {
            requiresAuth: true,
            title: 'Job forudsigelser'
        }
    },
    {
        path: '/dine-ferieperioder',
        name: 'Ferieperioder',
        component: () => import('@/views/portal/Ferieperioder'),
        meta: {
            requiresAuth: true,
            title: 'Ferieperioder'
        }
    },
    {
        path: '/ekstraphone',
        name: 'Ekstraphone',
        component: () => import('@/views/portal/ekstraphone'),
        meta: {
            requiresAuth: true,
            title: 'Ekstra telefon'
        }
    },
    {
        path: '/ekstraemail',
        name: 'Ekstraemail',
        component: () => import('@/views/portal/ekstraemail'),
        meta: {
            requiresAuth: true,
            title: 'Ekstra email'
        }
    },
    {
        path: '/profilside',
        name: 'profilside',
        component: () => import('@/views/portal/Profilside'),
        meta: {
            requiresAuth: true,
            title: 'Profilside'
        }
    },
    {
        path: '/confirm-description',
        name: 'ConfirmDescription',
        component: () => import('@/views/portal/confirm-description'),
        meta: {
            requiresAuth: true,
            title: 'Bekræft beskrivelse'
        }
    },
    {
        path: '/rediger-kreditkort-oplysninger',
        name: 'kreditkort',
        component: () => import('@/views/portal/EditCreditCard'),
        meta: {
            requiresAuth: false,
            title: 'Rediger kreditkort'
        }
    },
    {
        path: '/visitkort',
        name: 'Køb streamers og visitkort',
        component: () => import('@/views/portal/EditvisitKort'),
        meta: {
            requiresAuth: true,
            title: 'Køb streamers og visitkort'
        }
    },
    {
        path: '/expire-subscription',
        name: 'Abonnement',
        component: () => import('@/views/portal/expireSubscriptionv2'),
        meta: {
            requiresAuth: true,
            title: 'Abonnement'
        }
    },
    {
        path: '/expire-subscription-form',
        name: 'AbonnementForm',
        component: () => import('@/views/portal/ExpireSubscriptionForm'),
        meta: {
            requiresAuth: true,
            title: 'Opsig abonnement'
        }
    },
    {
        path: '/expire-subscription/captcha',
        name: 'AbonnementCaptcha',
        component: () => import('@/views/portal/expireSubscriptionFinal'),
        meta: {
            requiresAuth: false,
            title: 'Bekræft opsigelse'
        }
    },
    {
        path: '/leverandoerservice',
        name: 'Betalingsaftale',
        redirect: '/rediger-kreditkort-oplysninger',
        meta: {
            requiresAuth: true
        }
    },
    {
        path: '/konto-update',
        name: 'kontoupdate',
        component: () => import('@/views/portal/payproffv2'),
        meta: {
            requiresAuth: true,
            title: 'Konto opdatering'
        }
    },
    {
        path: '/konto-kyc',
        name: 'Payproff Kyc',
        component: () => import('@/views/portal/payproff_kyc'),
        meta: {
            requiresAuth: true,
            title: 'KYC verifikation'
        }
    },
    {
        path: '/inviter-en-kollega',
        name: 'Inviter en kollega',
        component: () => import('@/views/portal/InviterKollega'),
        meta: {
            requiresAuth: true,
            title: 'Inviter en kollega'
        }
    },
    {
        path: '/invitation',
        name: 'Invitation',
        component: () => import('@/views/portal/invite'),
        meta: {
            requiresAuth: true,
            title: 'Invitation'
        }
    }
]

// =============================================================================
// MESSAGING ROUTES
// =============================================================================
const messageRoutes = [
    {
        path: '/inbox',
        name: 'Inbox',
        component: () => import('@/views/portal/Inbox'),
        meta: {
            requiresAuth: true,
            title: 'Indbakke'
        }
    },
    {
        path: '/email',
        name: 'Email',
        component: () => import('@/views/portal/email'),
        meta: {
            requiresAuth: true,
            title: 'Email'
        }
    },
    {
        path: '/chat',
        name: 'Chat',
        component: () => import('@/views/portal/Chat'),
        meta: {
            requiresAuth: true,
            title: 'Chat'
        }
    },
    {
        path: '/messages/inbox',
        name: 'Indbakke',
        component: () => import('@/views/portal/messages/inboxv2'),
        meta: {
            requiresAuth: true,
            title: 'Indbakke'
        }
    },
    {
        path: '/messages/sent',
        name: 'Sendt',
        component: () => import('@/views/portal/messages/sent'),
        meta: {
            requiresAuth: true,
            title: 'Sendte beskeder'
        }
    },
    // {
    //     path: '/messages/notifications',
    //     name: 'Notifikationer',
    //     component: () => import('@/views/portal/Notifications'),
    //     meta: {
    //         requiresAuth: true,
    //         title: 'Notifikationer'
    //     }
    // }
]

// =============================================================================
// WEBSHOP ROUTES
// =============================================================================
const webshopRoutes = [
    {
        path: '/webshop',
        name: 'Webshop',
        // redirect: '/',
        component: () => import('@/views/portal/webshopv2/overview'),
        meta: {
            requiresAuth: true
        }
    },
    {
        path: '/webshop/cart',
        name: 'webshopCart',
        // redirect: '/',
        component: () => import('@/views/portal/webshopv2/cart'),
        meta: {
            requiresAuth: true,
            title: 'Kurv',
            deprecated: true
        }
    },
    {
        path: '/webshop/product/:ID',
        name: 'webshopProduct',
        // redirect: '/',
        component: () => import('@/views/portal/webshopv2/product'),
        meta: {
            requiresAuth: true,
            title: 'Produkt',
            deprecated: true
        }
    },
    {
        path: '/webshop/overview',
        name: 'WebShop Oversigt',
        component: () => import('@/views/portal/webshopv2/overview'),
        meta: {
            requiresAuth: true,
            title: 'Webshop oversigt'
        }
    },
    {
        path: '/webshop/product',
        name: 'Product',
        component: () => import('@/views/portal/webshop/product'),
        meta: {
            requiresAuth: true,
            title: 'Produkt'
        }
    },
    {
        path: '/webshop/products',
        name: 'Products',
        component: () => import('@/views/portal/webshop/products'),
        meta: {
            requiresAuth: true,
            title: 'Produkter'
        }
    },
    {
        path: '/webshop/checkout',
        name: 'Checkout',
        component: () => import('@/views/portal/webshop/checkout'),
        meta: {
            requiresAuth: true,
            title: 'Checkout'
        }
    },
    {
        path: '/webshop/orders',
        name: 'Orders',
        component: () => import('@/views/portal/webshop/orders'),
        meta: {
            requiresAuth: true,
            title: 'Ordrer'
        }
    },
    {
        path: '/webshop/order',
        name: 'Order',
        component: () => import('@/views/portal/webshop/order'),
        meta: {
            requiresAuth: true,
            title: 'Ordre'
        }
    },
    {
        path: '/webshop/wishlist',
        name: 'Wish List',
        component: () => import('@/views/portal/webshop/wishlist'),
        meta: {
            requiresAuth: true,
            title: 'Ønskeliste'
        }
    },
    {
        path: '/webshop/fb-ads',
        name: 'Fb Ads',
        component: () => import('@/views/portal/webshop/FbAds'),
        meta: {
            requiresAuth: true,
            title: 'Facebook annoncer'
        }
    },
    {
        path: '/webshop/google-ads',
        name: 'Google Ads',
        component: () => import('@/views/portal/webshop/GoogleAds'),
        meta: {
            requiresAuth: true,
            title: 'Google annoncer'
        }
    },
    {
        path: '/webshop/boost-profilside',
        name: 'Boost profileside',
        component: () => import('@/views/portal/webshop/BoostProfilside'),
        meta: {
            requiresAuth: true,
            title: 'Boost profilside'
        }
    },
    {
        path: '/webshop/boost-projekt',
        name: 'Boost projekt',
        component: () => import('@/views/portal/webshop/BoostProjekt'),
        meta: {
            requiresAuth: true,
            title: 'Boost projekt'
        }
    },
    {
        path: '/webshop/boost-en-anbefaling',
        name: 'Boost anbefale',
        component: () => import('@/views/portal/webshop/BoostEnAnbefaling'),
        meta: {
            requiresAuth: true,
            title: 'Boost anbefaling'
        }
    },
    {
        path: '/webshop/terms-and-conditions',
        name: 'Terms and Conditions',
        component: () => import('@/views/portal/webshop/TermsAndConditions'),
        meta: {
            requiresAuth: true,
            title: 'Vilkår og betingelser'
        }
    }
]

// =============================================================================
// KLIMA (CLIMATE) ROUTES
// =============================================================================
const klimaRoutes = [
    {
        path: '/klima',
        name: 'Klima',
        component: () => import('@/views/portal/klima/klima'),
        meta: {
            requiresAuth: true,
            title: 'Klima'
        }
    },
    {
        path: '/klima/klima',
        name: 'Klima Plan',
        component: () => import('@/views/portal/klima/klima'),
        meta: {
            requiresAuth: true,
            title: 'Klimaplan'
        }
    },
    {
        path: '/klima/checklist',
        name: 'Klima Checklist',
        component: () => import('@/views/portal/klima/checklist'),
        meta: {
            requiresAuth: true,
            title: 'Klima tjekliste'
        }
    },
    {
        path: '/klima/survey',
        name: 'Klima - Customer Checklist',
        component: () => import('@/views/portal/klima/survey'),
        meta: {
            requiresAuth: true,
            title: 'Klima kundetjekliste'
        }
    },
    {
        path: '/klima/editsurvey',
        name: 'Klima - Edit Checklist',
        component: () => import('@/views/portal/klima/editsurvey'),
        meta: {
            requiresAuth: true,
            title: 'Rediger tjekliste'
        }
    },
    {
        path: '/klima/registration',
        name: 'Klima - Register Fag',
        component: () => import('@/views/portal/klima/registration'),
        meta: {
            requiresAuth: true,
            title: 'Registrer fag'
        }
    },
    {
        path: '/klima/documentation',
        name: 'Documentation',
        component: () => import('@/views/portal/klima/dokumentation'),
        meta: {
            requiresAuth: true,
            title: 'Dokumentation'
        }
    }
]

// =============================================================================
// MISC & UTILITY ROUTES
// =============================================================================
const miscRoutes = [
    {
        path: '/finansiering',
        name: 'Finansiering',
        component: () => import('@/views/portal/Finansiering'),
        meta: {
            requiresAuth: true,
            title: 'Finansiering'
        }
    },
    {
        path: '/kredit-tjek',
        name: 'Kredittjek',
        component: () => import('@/views/portal/CreditCheck'),
        meta: {
            requiresAuth: true,
            title: 'Kredittjek'
        }
    },
    {
        path: '/jule',
        name: 'Jule',
        component: () => import('@/views/portal/jule'),
        meta: {
            requiresAuth: true,
            title: 'Julekampagne'
        }
    },
    {
        path: '/medlemsfordele',
        name: 'Medlemsfordele',
        component: () => import('@/views/portal/medlemsfordelev2'),
        meta: {
            requiresAuth: true,
            title: 'Medlemsfordele'
        }
    },
    {
        path: '/badges',
        name: 'digital-pakke',
        component: () => import('@/views/portal/badges'),
        meta: {
            requiresAuth: true,
            title: 'Badges'
        }
    },
    {
        path: '/Artikel',
        name: 'pressArticle',
        component: () => import('@/views/portal/press-article'),
        meta: {
            requiresAuth: true,
            title: 'Presseartikel'
        }
    },
    {
        path: '/ordrestyring',
        name: 'Ordrestyring',
        component: () => import('@/views/portal/ordrestyring'),
        meta: {
            requiresAuth: true,
            title: 'Ordrestyring'
        }
    },
    {
        path: '/uddannelsesbevis',
        name: 'UddanelseBevis',
        component: () => import('@/views/portal/UddanelseBevis'),
        meta: {
            requiresAuth: true,
            title: 'Uddannelsesbevis'
        }
    },
    {
        path: '/billederside',
        name: 'Diplomas',
        component: () => import('@/views/portal/BillederSide'),
        meta: {
            requiresAuth: true,
            title: 'Diplomer'
        }
    },
    {
        path: '/stamoplysninger',
        name: 'stamoplysninger',
        redirect: '/',
        meta: {
            requiresAuth: true
        }
    }
]

// =============================================================================
// COMBINE ALL ROUTES
// =============================================================================
const routes = [
    ...authRoutes,
    ...micrositeRoutes,
    ...coreRoutes,
    ...marketplaceRoutes,
    ...projectRoutes,
    ...mesterRoutes,
    ...contractRoutes,
    ...tilbudRoutes,
    ...anbefalingerRoutes,
    ...settingsRoutes,
    ...messageRoutes,
    ...webshopRoutes,
    ...klimaRoutes,
    ...miscRoutes
]

export default routes
